import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';

import FilterCriteriasQuery from 'assets/graphql/queries/FilterCriterias.graphql';
import BoxActivitiesMapLocations from 'assets/graphql/queries/BoxActivitiesMapLocations.graphql';
import areContiguous from 'services/areContiguous';
import ExperienceBox from 'components/ExperienceBox';
import FilterExperiences from 'components/FilterExperiences';
import cleanString from 'common/services/cleanString';
import { useBoxCatalogViewStore } from 'stores/boxCatalogView.store.js';

import {
  CircleRadius,
  InfoWindow,
  Marker,
} from 'common/components/Map/BbMap/Objects';
import bbMap from 'common/components/Map/BbMap';
import WhiteMarker from 'assets/images/map-marker.png';
import BbModal from 'common/components/BbModal';
import useBoxCatalogView from 'composables/useBoxCatalogView';
import CatalogReservationConfigModal from 'components/Catalog/CatalogReservationConfigModal/CatalogReservationConfigModal.vue';
import customCatalogs from 'constants/customCatalogs';
import { useAccountStore } from 'stores/account';
import useCatalogFilters from 'composables/useCatalogFilters';
import ExperienceFilter from 'components/ExperienceFilter';

@Component({
  apollo: {
    filterCriterias: {
      query: FilterCriteriasQuery,
      variables() {
        return { boxId: this.box.id };
      },
      update(data) {
        return {
          provinces: data.provinces,
          categories: data.box_activities_categories,
        };
      },
      result({ data }) {
        if (this.$route.query.province) {
          const province = data.provinces.find(_province => {
            return this.$route.query.province === _province.slug;
          });
          if (province) {
            this.selectedLocation = province.id;
          }
        }
        if (this.$route.query.category) {
          const categoryData = data.categories;
          this.selectedCategory = categoryData.id;
        }
      },
    },
    map_locations: {
      query: BoxActivitiesMapLocations,
      variables() {
        return {
          box_slug: this.$route.params.boxSlug,
          order_by: this.sortCriteria && this.sortCriteria,
          provinceId:
            this.selectedLocation === '0' ? undefined : this.selectedLocation,
          search_term: this.searchQuery,
        };
      },
      update(data) {
        return data.paginated_box_activities.results;
      },
      skip() {
        return this.skipQuery;
      },
    },
  },
  props: {
    box: {
      type: Object,
      required: true,
    },
    expirationDate: {
      type: String,
      required: true,
    },
    infiniteScroll: {
      type: Boolean,
      default: true,
    },
    gift: {
      type: Object,
      required: false,
    },
    booked: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ExperienceBox,
    FilterExperiences,
    bbMap,
    BbModal,
    CatalogReservationConfigModal,
    ExperienceFilter,
    ...customCatalogs,
  },
  setup() {
    const catalogViewStore = useBoxCatalogViewStore();

    const {
      getActivityBoxIdSelected,
      onActivityBySlug,
      onCreateReservation,
      getOtherActivities,
    } = useBoxCatalogView();

    const {
      getFacets,
      getCurrentFilters,
      getCurrentTags,
      getOptionCards,
      getCardSelected,
      getResults,
      getLoading,
      getPageInfo,
      setKeywords,
      getBoxes,
      getActivities,
      getTravel,
      getDataFromFilters,
      setCard,
      setBoxCategory,
      clearCurrentFilters,
      setBoxId,
      getCatalogCodeComponent,
      getCatalogModalComponent,
      getModalReservationFlag,
      onToggleModalReservation,
      onToggleModalPreview,
    } = useCatalogFilters();

    return {
      catalogViewStore,
      getModalReservationFlag,
      onToggleModalPreview,
      activityBoxIdSelected: getActivityBoxIdSelected,
      toggleModalReservation: onToggleModalReservation,
      catalogCodeComponent: getCatalogCodeComponent,
      catalogModalComponent: getCatalogModalComponent,
      activityBySlug: onActivityBySlug,
      createReservation: onCreateReservation,
      getOtherActivities,
      facets: getFacets,
      currentFilters: getCurrentFilters,
      currentTags: getCurrentTags,
      cardSelected: getCardSelected,
      optionCards: getOptionCards,
      results: getResults,
      loading: getLoading,
      pageInfo: getPageInfo,
      getBoxes,
      setKeywords,
      getActivities,
      getTravel,
      getDataFromFilters,
      setCard,
      setBoxCategory,
      clearCurrentFilters,
      setBoxId,
      onToggleModalReservation,
    };
  },
})
export default class BoxActivitiesResultsComponent extends Vue {
  selectedLocation = '0';
  selectedCategory = '0';
  sortCriteria = undefined;
  searchQuery = '';
  skipQuery = true;
  changeFilter = false;
  positionSelected = -1;
  showPreview = false;
  showMap = false;
  mapLoading = false;
  showHomeActivities = false;
  infoWindow = new InfoWindow({
    component: 'BbGoogleMapCustomInfoWindow',
  });
  timer = null;
  timeout = 500;
  boxCustomizerShowOnlineReservation = null;
  otherActivities = true;

  async getActivities() {
    this.setBoxId(this.box.id);
  }

  async mounted() {
    await this.getActivities();
    if (this.infiniteScroll) {
      window.addEventListener('scroll', this.onInfinityScroll);
    }
    this.boxCustomizerShowOnlineReservation =
      this.box.boxcustomizer?.show_online_reservation ?? true;
  }

  beforeDestroy() {
    this.catalogViewStore.$reset();
    if (this.infiniteScroll) {
      window.removeEventListener('scroll', this.onInfinityScroll);
      clearInterval(this.timer);
    }
    if (document.getElementById('site-overlay')) {
      this.$emit('showOverlay');
    }
  }

  get experienceListTitle() {
    if (this.catalogViewStore.getTotalActivities > 1) {
      return this.catalogViewStore.getTotalActivities;
    } else if (this.catalogViewStore.getTotalActivities === 1) {
      return '1 experiencia para elegir';
    } else if (
      this.catalogViewStore.getTotalActivities === 0 &&
      this.searchQuery
    ) {
      return 'Ninguna experiencia coincide con tu búsqueda';
    }
    return 'No hay experiencias';
  }

  get authUser() {
    const account = useAccountStore();
    return account.getUserData;
  }

  /**
   * changePositionSelected is issued from the child and allows me
   * to get the new box selected.
   */
  changePositionSelected(obj) {
    if (window.innerWidth > 768) {
      if (
        !areContiguous(this.positionSelected, obj.newPosition) &&
        obj.showPreview
      ) {
        const experienceBoxY = obj.experienceElement.getBoundingClientRect().y;
        if (this.showPreview && obj.newPosition > this.positionSelected) {
          window.scrollBy({
            top: experienceBoxY - 80,
            left: 0,
            behavior: 'smooth',
          });
        } else {
          // top: experienceBoxY + 325,
          window.scrollBy({
            top: experienceBoxY + 200,
            left: 0,
            behavior: 'smooth',
          });
        }
      }
    } else {
      this.$emit('showOverlay');
    }
    this.showPreview = obj.showPreview;
    this.positionSelected = obj.newPosition;
  }

  /**
   * It shows me a canvas with low opacity that covers all the boxes in the mobile version.
   */
  showCanvas() {
    this.showPreview = !this.showPreview;
    this.positionSelected = -1;
  }

  async removeFilters() {
    this.searchQuery = '';
    this.selectedLocation = '0';
    this.sortCriteria = '';
    this.catalogViewStore.resetFilters();
    this.$refs.filterExperiences.clearSearchQuery();
    this.showHomeActivities = false;
    await this.updateActivities();
  }

  /**
   * Event received from FilterExperiences
   */
  async updateLocation(location) {
    this.selectedLocation = location;
    this.catalogViewStore.onSetProvince(this.selectedLocation);
    await this.updateActivities();
  }

  async updateCategory(category_id) {
    this.selectedCategory = category_id;
    this.catalogViewStore.onSetCategory(this.selectedCategory);
    await this.updateActivities();
  }
  async toggleMap() {
    if (this.showPreview) {
      this.showPreview = false;
    }
    if (!this.showMap) {
      this.mapLoading = true;
      this.$apollo.queries.map_locations.skip = false;
      await this.$apollo.queries.map_locations.refetch();
      this.mapLoading = false;
    }
    this.showMap = !this.showMap;
  }

  /**
   * Event received from FilterExperiences
   */
  async updateSort(sort) {
    this.sortCriteria = sort.criteria ? sort.criteria : '';
    this.catalogViewStore.onSetOrderBy(this.sortCriteria);
    await this.updateActivities();
  }

  async updateFilter() {
    this.showHomeActivities = !this.showHomeActivities;
    this.catalogViewStore.onSetAtHome(this.showHomeActivities);
    await this.updateActivities();
  }

  /**
   * Event received from FilterExperiences
   */
  updateSearchQuery(query) {
    clearTimeout(this.timer);
    this.timer = setTimeout(async () => {
      this.searchQuery = cleanString(query);
      this.catalogViewStore.onSetSearchTerm(this.searchQuery);
      await this.updateActivities();
    }, this.timeout);
  }

  /**
   * Event listener to know when the user scroll to the bottom of the screen to get more boxes.
   */
  onInfinityScroll() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      if (
        this.catalogViewStore.getHasNextPage &&
        !this.catalogViewStore.getLoading
      ) {
        const bottomOff =
          this.$refs.loadmore?.getBoundingClientRect?.()?.bottom -
          window.screen.height;

        if (bottomOff < 50 && !this.catalogViewStore.getLoading) {
          this.fetchMoreActivities();
        }
        this.scrollPosition = window.scrollY;
      }
    }, 200);
  }

  async fetchActivities() {
    await this.catalogViewStore.getPaginatedBoxActivities(
      this.$route.params.boxSlug,
    );
    if (!this.catalogViewStore.getHasNextPage)
      this.$emit('reachedLastPage', true);
  }

  async fetchMoreActivities() {
    this.catalogViewStore.onSetPage(
      this.catalogViewStore.getPageInfo.number + 1,
    );
    await this.fetchActivities();
  }

  async updateActivities() {
    this.changeFilter = true;
    this.catalogViewStore.paginatedActivities.allActivitiesResults = [];
    await this.fetchActivities();
    this.changeFilter = false;
  }

  async fetchActivityBySlug(activitySlug) {
    // get activity by slug
    this.activityBySlug(activitySlug);
  }

  onCloseReservationModal() {
    this.onToggleModalPreview(this.catalogModalComponent?.props?.activity);
    this.onToggleModalReservation();
  }

  get allProvinces() {
    const newAllProvinces = {};
    this.filterCriterias.provinces.forEach(item => {
      newAllProvinces[item.id] = item.name;
    });
    return newAllProvinces;
  }

  get mapLocations() {
    const markers = [];
    this.map_locations.forEach(boxActivity => {
      const { activity } = boxActivity;
      activity.locations.forEach(location => {
        const marker = new Marker({
          id: location.id,
          position: { lat: location.latitude, lng: location.longitude },
          infoWindowContext: activity,
        });
        if (activity.activity_type === 'delivery' && location.delivery_radius) {
          marker.circleRadius = new CircleRadius({
            radius: location.delivery_radius,
            center: { lat: location.latitude, lng: location.longitude },
          });
        }
        marker.settings.icon.url = WhiteMarker;
        marker.settings.customMarker = true;
        marker.settings.label = {
          text: String(activity.rating || 'Nueva'),
          color: '#FDC701',
          fontWeight: 'bold',
          fontSize: '12px',
        };
        markers.push(JSON.parse(JSON.stringify(marker)));
      });
    });
    return markers;
  }

  @Watch('$route')
  onRouteChanged() {
    this.showMap = false;
    this.selectedLocation = '0';
  }
}
